import React, { useRef, useState } from 'react';
import toast from '../../../../utils/toast';
import { createUnit, updateUnit } from '../../../../api/hierarchy-client';
import { HierarchyApiError } from '../../../../api/hierarchy-client';
import {
  CreateUnitInput,
  Unit,
  UNIT_KINDS,
  UNIT_STATUSES,
  UnitKind,
  UnitStatus,
  UpdateUnitInput,
} from '../../../../interfaces/hierarchy-interfaces';
import {
  InlineMessage,
  StatusLabel,
  scrimMotion,
  useOverlayMotion,
} from '../../../../motion';
import {
  Banner,
  Field,
  FieldError,
  Form,
  FormRow,
  FormRowThree,
  GhostButton,
  Hint,
  Input,
  Label,
  Modal,
  ModalActions,
  ModalHeader,
  ModalSub,
  ModalTitle,
  PrimaryButton,
  Scrim,
  Select,
  Textarea,
} from './dashboard-units.styled';
import {
  AREA_UNIT_LABEL,
  MeasurementSystem,
  areaToSqFt,
  readError,
  readMeasurementSystem,
  sqFtToAreaInput,
  unitKindLabel,
  UNIT_STATUS_LABELS,
} from './units-shared';
import { useDialogFocus } from '../../../hooks/use-dialog-focus';
import { ModalPortal } from '../../../modal/modal-portal';
import { EntityGallery } from '../../../gallery/entity-gallery';
import { useAuth } from '../../../../context/auth-provider';
import { can, PERMISSIONS } from '../../../../auth/permissions';

/**
 * Create / edit a unit.
 *
 * `unit` present means edit, absent means create — the two differ only in the
 * endpoint and in `status`, which is writable on PUT but not on POST.
 *
 * unit_number_norm is DELIBERATELY absent from this form. It is a STORED
 * GENERATED column (upper(replace(btrim(unit_number),' ',''))); Postgres
 * rejects any write naming it, so sending it is at best ignored and at worst a
 * 500. The user edits unit_number and the database derives the rest.
 *
 * ── PHOTOS, AND WHY ONLY WHEN EDITING ─────────────────────────────────────
 * A unit's gallery used to exist ONLY on /dashboard/units/:id. Every route into
 * a unit from the buildings screen stops at this dialog, so somebody working
 * through a building's units had no way to reach the photos at all — the
 * feature was built and then unreachable from the place people actually manage
 * units.
 *
 * It renders only on EDIT because the gallery is addressed by unit id, and on
 * create there is not one yet. Uploading against a unit that does not exist is
 * not a thing to fake with a queue: the photos would have to be held in memory,
 * re-posted after the insert, and reconciled if that second call failed. So the
 * dialog says plainly that photos come after the unit does.
 *
 * IT DOES NOT WAIT FOR "Save changes", and it says so. EntityGallery writes
 * straight to POST /images the moment a crop is confirmed — that is what makes
 * the reorder, cover and delete controls honest on the unit page too. Somebody
 * who adds a photo and then presses Cancel has still added the photo, and a
 * dialog that let them believe otherwise would be the worse bug.
 */

interface UnitFormProps {
  buildingId: number;
  // null = create a new unit in `buildingId`.
  unit: Unit | null;
  onClose: () => void;
  onSaved: () => void;
}

interface FormState {
  unit_number: string;
  name: string;
  kind: UnitKind;
  floor: string;
  bedrooms: string;
  bathrooms: string;
  sq_ft: string;
  status: UnitStatus;
  notes: string;
}

// `system` only decides what the size box is SEEDED with and labelled as. The
// column is always square feet; see units-shared.
const toFormState = (
  unit: Unit | null,
  system: MeasurementSystem
): FormState => ({
  unit_number: unit?.unit_number ?? '',
  name: unit?.name ?? '',
  kind: unit?.kind ?? 'residential',
  floor: unit?.floor ?? '',
  bedrooms: unit?.bedrooms === null || unit === null ? '' : `${unit.bedrooms}`,
  bathrooms: unit?.bathrooms ?? '',
  sq_ft:
    unit?.sq_ft === null || unit === null
      ? ''
      : sqFtToAreaInput(unit.sq_ft, system),
  status: unit?.status ?? 'active',
  notes: unit?.notes ?? '',
});

// An emptied optional field must travel as null to CLEAR the column; sending ''
// would store an empty string and sending undefined would leave the old value
// in place on the PATCH-style PUT.
const optionalText = (value: string): string | null => {
  const trimmed = value.trim();

  return trimmed === '' ? null : trimmed;
};

const optionalInt = (value: string): number | null => {
  const trimmed = value.trim();

  return trimmed === '' ? null : Number(trimmed);
};

export const DashboardUnitForm: React.FC<UnitFormProps> = ({
  buildingId,
  unit,
  onClose,
  onSaved,
}) => {
  const isEdit = unit !== null;
  // Read here rather than passed in: this dialog is opened from the units list
  // AND from inside the building modal, and a prop would have to be threaded
  // correctly through both. The server gates unit images on unit:edit
  // (lobbero_be/src/CRUD/entity-images.js:81), so this asks the same question.
  const { permissions }: { permissions: string[] } = useAuth();
  const canEditUnit = can(permissions, PERMISSIONS.UNIT_EDIT);
  // Read once and FROZEN for the life of the dialog. If this followed the live
  // preference, flipping the toggle behind the modal would reinterpret a
  // half-typed number as the other unit and quietly change what gets saved.
  const [system] = useState<MeasurementSystem>(readMeasurementSystem);
  const [form, setForm] = useState<FormState>(() => toFormState(unit, system));
  // What the size box was seeded with. An edit that never touches it must store
  // the ORIGINAL integer rather than a re-conversion of the rounded display
  // value — otherwise re-saving a metric unit walks it 1075 → 1074 → 1073 sq ft,
  // a field the user never looked at losing a foot per save.
  const initialSize = useRef(form.sq_ft);
  const [saving, setSaving] = useState(false);
  const saveRef = useRef(false);
  // Split deliberately: the duplicate-number 409 belongs ON the field that
  // caused it, everything else belongs in the banner.
  const [numberError, setNumberError] = useState('');
  const [formError, setFormError] = useState('');
  const dialogRef = useDialogFocus<HTMLDivElement>(true, () => {
    if (!saveRef.current) {
      onClose();
    }
  });
  /* A centred modal that becomes a bottom sheet on a phone. The caller renders
     this component inside a Presence with a key, which is what lets the scrim
     and the card animate OUT on close. */
  const modalMotion = useOverlayMotion('modal');

  const change = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = event.target;

    setForm(current => ({ ...current, [name]: value }));

    if (name === 'unit_number') {
      setNumberError('');
    }
  };

  const validate = (): boolean => {
    if (!form.unit_number.trim()) {
      setNumberError('A unit number is required.');

      return false;
    }

    const bedrooms = form.bedrooms.trim();

    if (bedrooms !== '' && !Number.isInteger(Number(bedrooms))) {
      setFormError('Bedrooms must be a whole number.');

      return false;
    }

    const bathrooms = form.bathrooms.trim();

    if (bathrooms !== '' && Number.isNaN(Number(bathrooms))) {
      setFormError('Bathrooms must be a number, for example 1.5.');

      return false;
    }

    const size = form.sq_ft.trim();

    if (size !== '') {
      const value = Number(size);

      // The column is CHECK (sq_ft > 0), so zero is not "no size" — an empty
      // box is. Catching it here keeps that distinction out of a 500.
      if (Number.isNaN(value) || value <= 0) {
        setFormError(
          `Size must be a number greater than zero, in ${AREA_UNIT_LABEL[system]}.`
        );

        return false;
      }

      // Only feet are constrained to whole numbers — 79.5 m² is an ordinary way
      // to write an apartment, and it converts to an integer count of feet.
      if (system === 'imperial' && !Number.isInteger(value)) {
        setFormError('Square footage must be a whole number.');

        return false;
      }
    }

    return true;
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (saveRef.current) {
      return;
    }

    setNumberError('');
    setFormError('');

    if (!validate()) {
      return;
    }

    const size = form.sq_ft.trim();
    const sqFt =
      size === ''
        ? null
        : size === initialSize.current
        ? // Untouched: keep exactly what the server sent us.
          unit?.sq_ft ?? null
        : areaToSqFt(Number(size), system);

    const payload: CreateUnitInput = {
      unit_number: form.unit_number.trim(),
      name: optionalText(form.name),
      kind: form.kind,
      floor: optionalText(form.floor),
      bedrooms: optionalInt(form.bedrooms),
      bathrooms: optionalText(form.bathrooms),
      sq_ft: sqFt,
      notes: optionalText(form.notes),
    };

    saveRef.current = true;
    setSaving(true);
    try {
      if (unit) {
        const update: UpdateUnitInput = { ...payload, status: form.status };

        await updateUnit(unit.id, update);
        toast.success(`Unit ${payload.unit_number} updated`);
      } else {
        await createUnit(buildingId, payload);
        toast.success(`Unit ${payload.unit_number} added`);
      }

      onSaved();
    } catch (error) {
      // The server's message names the colliding number; it is strictly more
      // useful than anything invented here.
      if (
        error instanceof HierarchyApiError &&
        error.conflict === 'unit_number_taken'
      ) {
        setNumberError(error.message);
      } else {
        setFormError(readError(error, 'Could not save this unit.'));
      }
    } finally {
      saveRef.current = false;
      setSaving(false);
    }
  };

  return (
    <ModalPortal>
      <Scrim
        {...scrimMotion}
        onMouseDown={event => {
          if (event.target === event.currentTarget && !saveRef.current) {
            onClose();
          }
        }}
      >
        {/* The dialog role lives on the CARD, the thing that moves and holds
            focus; the scrim is only the dim behind it. */}
        <Modal
          {...modalMotion}
          ref={dialogRef}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-label={isEdit ? 'Edit unit' : 'Add unit'}
          onMouseDown={event => event.stopPropagation()}
        >
          <ModalHeader>
            <ModalTitle>{isEdit ? 'Edit unit' : 'Add a unit'}</ModalTitle>
            <ModalSub>
              {isEdit
                ? 'Details apply to the unit itself. Who lives here is managed from the unit page.'
                : 'Only the unit number is required. A name is worth adding — it is what the front desk reads.'}
            </ModalSub>
          </ModalHeader>

          <Form onSubmit={submit} noValidate>
            <Field>
              <Label htmlFor="unit_number">Unit number</Label>
              <Input
                id="unit_number"
                name="unit_number"
                value={form.unit_number}
                onChange={change}
                placeholder="4B"
                autoFocus
                invalid={!!numberError}
                aria-invalid={!!numberError}
                aria-describedby={
                  numberError ? 'unit_number-error' : 'unit_number-hint'
                }
              />
              <FieldError id="unit_number-error" message={numberError} />
              {!numberError && (
                <Hint id="unit_number-hint">
                  Spacing and case are ignored when checking for duplicates — 4b
                  and 4 B are the same unit.
                </Hint>
              )}
            </Field>

            <Field>
              {/* "(optional)" moved into the hint — the label leads with the
                  encouragement, not the excuse. Still no `required`. */}
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                name="name"
                value={form.name}
                onChange={change}
                placeholder="Penthouse"
                maxLength={120}
              />
              <Hint>
                Shown under the number wherever the unit appears — worth setting
                for a Penthouse, a Studio A, or whatever the building actually
                calls it. Optional.
              </Hint>
            </Field>

            <FormRow>
              <Field>
                <Label htmlFor="kind">Kind</Label>
                <Select
                  id="kind"
                  name="kind"
                  value={form.kind}
                  onChange={change}
                >
                  {UNIT_KINDS.map(kind => (
                    <option key={kind} value={kind}>
                      {unitKindLabel(kind)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field>
                <Label htmlFor="floor">Floor</Label>
                <Input
                  id="floor"
                  name="floor"
                  value={form.floor}
                  onChange={change}
                  placeholder="4"
                />
              </Field>
            </FormRow>

            <FormRowThree>
              <Field>
                <Label htmlFor="bedrooms">Bedrooms</Label>
                <Input
                  id="bedrooms"
                  name="bedrooms"
                  type="number"
                  min="0"
                  step="1"
                  value={form.bedrooms}
                  onChange={change}
                  placeholder="2"
                />
              </Field>
              <Field>
                <Label htmlFor="bathrooms">Bathrooms</Label>
                <Input
                  id="bathrooms"
                  name="bathrooms"
                  type="number"
                  min="0"
                  step="0.5"
                  value={form.bathrooms}
                  onChange={change}
                  placeholder="1.5"
                />
              </Field>
              <Field>
                <Label htmlFor="sq_ft">Size ({AREA_UNIT_LABEL[system]})</Label>
                <Input
                  id="sq_ft"
                  name="sq_ft"
                  type="number"
                  min="0"
                  step={system === 'imperial' ? '1' : '0.1'}
                  value={form.sq_ft}
                  onChange={change}
                  placeholder={system === 'imperial' ? '850' : '79'}
                />
              </Field>
            </FormRowThree>

            {isEdit && (
              <Field>
                <Label htmlFor="status">Status</Label>
                <Select
                  id="status"
                  name="status"
                  value={form.status}
                  onChange={change}
                >
                  {/* 'archived' IS NOT OFFERED. Under R-20 it is the state a
                      Delete produces, so choosing it from a status dropdown
                      would be a second, quieter way to delete a unit — the
                      thing R-20a rules out. It stays in the list only when the
                      unit is already in it, so opening a legacy archived unit
                      does not silently reassign its status on save. */}
                  {UNIT_STATUSES.filter(
                    value => value !== 'archived' || form.status === 'archived'
                  ).map(value => (
                    <option key={value} value={value}>
                      {UNIT_STATUS_LABELS[value]}
                    </option>
                  ))}
                </Select>
                <Hint>
                  Offline keeps the unit and its number but takes it out of
                  availability — for a renovation or a hold. To remove a unit
                  altogether, use Delete on its row.
                </Hint>
              </Field>
            )}

            <Field>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                name="notes"
                value={form.notes}
                onChange={change}
                placeholder="Anything the front desk should know about this unit."
              />
            </Field>

            {/* ── PHOTOS ────────────────────────────────────────────────────
                Inside the <form> is safe: every control EntityGallery renders
                carries type="button" (entity-gallery.tsx:314/326/340), so none
                of them submits this form. Its own dialogs — the cropper, the
                lightbox, the delete confirmation — all portal to <body> and
                register on the shared dialog stack, so Escape closes the top
                one and leaves this dialog (and the building modal under it)
                standing. */}
            {isEdit && unit && (
              <Field>
                <EntityGallery
                  type="unit"
                  entityId={unit.id}
                  canEdit={canEditUnit}
                  title="Unit photos"
                />
                <Hint>
                  Photos are saved as soon as you add them — they do not wait
                  for Save changes, and Cancel will not remove them.
                </Hint>
              </Field>
            )}

            {!isEdit && (
              <Hint>
                Photos can be added once the unit exists — add it, then reopen
                it to attach them.
              </Hint>
            )}

            <InlineMessage
              message={
                formError ? <Banner tone="alert">{formError}</Banner> : undefined
              }
            />

            <ModalActions>
              <GhostButton type="button" onClick={onClose} disabled={saving}>
                Cancel
              </GhostButton>
              {/* The label swaps WITHOUT the button changing width — a spinner
                  in place of the words used to shrink it mid-save. */}
              <PrimaryButton type="submit" disabled={saving} aria-busy={saving}>
                <StatusLabel
                  state={saving ? 'busy' : 'idle'}
                  labels={{
                    idle: isEdit ? 'Save changes' : 'Add unit',
                    busy: 'Saving…',
                  }}
                />
              </PrimaryButton>
            </ModalActions>
          </Form>
        </Modal>
      </Scrim>
    </ModalPortal>
  );
};
